/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { FormGroup, FormText, Label } from "reactstrap";
import { RadioButtons } from "./RadioButton";

export interface NumberRadioButtonsProps {
    setValue: (value: number | undefined) => void;
    value: number | undefined;
    label: string;
    description?: string;
    min: number;
    max: number;
    disabled?: boolean;
    options?: { value: string | boolean; label?: string; text?: string }[];
}

export default function NumberRadioButtons({
    setValue,
    value,
    label,
    description,
    min = 0,
    max = 10,
    disabled = false,
    options,
}: NumberRadioButtonsProps) {
    // Parse current value into integer and decimal/adjustment parts
    const { currentInteger, currentDecimal } = useMemo(() => {
        if (value === undefined || value === null) {
            return { currentInteger: undefined, currentDecimal: undefined };
        }

        if (options && options.length > 0) {
            // When using options, loop over all integer + adjustment combinations
            // to find the one that exactly equals the current value
            // Try all integer values from min to max
            for (let int = min; int <= max; int++) {
                // Try all adjustment options
                for (let optIdx = 0; optIdx < options.length; optIdx++) {
                    const opt = options[optIdx];
                    const optValue = parseFloat(String(opt.value));
                    if (!Number.isNaN(optValue)) {
                        const combinedValue = int + optValue;
                        // Check for exact match (using small epsilon for floating point comparison)
                        if (Math.abs(combinedValue - value) < Number.EPSILON * 10) {
                            return { currentInteger: int, currentDecimal: String(opt.value) };
                        }
                    }
                }
            }

            // If no exact match found, don't select any decimal option
            const floor = Math.floor(value);
            return {
                currentInteger: floor >= min && floor <= max ? floor : min,
                currentDecimal: undefined,
            };
        }

        // Default behavior: decimal part (0-9)
        const integer = Math.floor(value);
        const decimal = Math.round((value - integer) * 10);
        return {
            currentInteger: integer,
            currentDecimal: decimal === 0 ? "0" : decimal.toString(),
        };
    }, [value, options, min, max]);

    // Generate integer options (min to max inclusive)
    const integerOptions = useMemo(() => {
        const opts: { value: string; label: string }[] = [];
        for (let i = min; i <= max; i++) {
            opts.push({
                value: i.toString(),
                label: i.toString(),
            });
        }
        return opts;
    }, [min, max]);

    // Generate decimal/adjustment options
    const decimalOptions = useMemo(() => {
        if (options && options.length > 0) {
            // Use provided options
            return options.map((opt) => ({
                value: String(opt.value),
                label: opt.label || opt.text || String(opt.value),
            }));
        }
        // Default: 0 to 9
        const opts: { value: string; label: string }[] = [];
        for (let i = 0; i <= 9; i++) {
            opts.push({
                value: i.toString(),
                label: `.${i}`,
            });
        }
        return opts;
    }, [options]);

    const handleIntegerChange = (_, newValue: string | undefined) => {
        const integer = newValue !== undefined ? parseInt(newValue, 10) : undefined;
        let adjustment = 0;

        if (options && options.length > 0) {
            // When using options, parse the current adjustment
            const currentOptValue = currentDecimal;
            if (currentOptValue !== undefined) {
                const optValue = parseFloat(currentOptValue);
                if (!Number.isNaN(optValue)) {
                    adjustment = optValue;
                }
            }
        } else {
            // Default: decimal part
            const decimal = currentDecimal !== undefined ? parseInt(currentDecimal, 10) : 0;
            adjustment = decimal / 10;
        }

        if (integer !== undefined) {
            const combinedValue = integer + adjustment;
            setValue(combinedValue);
        } else {
            // If integer is cleared, clear the entire value
            setValue(undefined);
        }
    };

    const handleDecimalChange = (_, newValue: string | undefined) => {
        const integer = currentInteger !== undefined ? currentInteger : min;
        let adjustment = 0;

        if (options && options.length > 0) {
            // When using options, parse the adjustment value
            if (newValue !== undefined) {
                const optValue = parseFloat(newValue);
                if (!Number.isNaN(optValue)) {
                    adjustment = optValue;
                }
            }
        } else {
            // Default: decimal part
            const decimal = newValue !== undefined ? parseInt(newValue, 10) : 0;
            adjustment = decimal / 10;
        }

        const combinedValue = integer + adjustment;
        setValue(combinedValue);
    };

    return (
        <FormGroup>
            {label && <Label>{label}</Label>}
            {description && <FormText className="d-block">{description}</FormText>}

            <div className="mb-2">
                <RadioButtons
                    name="integer"
                    color="secondary"
                    current={currentInteger !== undefined ? currentInteger.toString() : undefined}
                    options={integerOptions}
                    onChange={handleIntegerChange}
                    disabled={disabled}
                />
            </div>

            <div>
                <RadioButtons
                    name="decimal"
                    color="secondary"
                    current={currentDecimal}
                    options={decimalOptions}
                    onChange={handleDecimalChange}
                    disabled={disabled}
                />
            </div>
        </FormGroup>
    );
}

